import { Alert, AlertDescription, Button, toast } from '@osteopro/ui';
import { Eye } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useAuth } from '../../lib/auth-provider.js';
import { errorMessage } from '../../lib/error-messages.js';

/** Où revient l'administrateur à la fin d'une séance : la liste d'où il l'a ouverte. */
export const IMPERSONATION_EXIT_PATH = '/plateforme/utilisateurs';

/**
 * Bandeau d'une séance « Voir comme ». Rendu en haut de toute page tant que la séance dure, murs
 * du compte regardé compris (adresse non vérifiée, cabinet suspendu…) : l'administrateur voit ce
 * que voit le compte, mais garde toujours la sortie.
 *
 * L'échéance est tenue par le serveur. Le minuteur ne fait que relire l'identité à l'heure dite,
 * pour que l'écran ne reste pas sur des données qu'il ne pourrait plus rafraîchir.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { user, impersonation, endImpersonation, reloadIdentity } = useAuth();
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (impersonation === null) return;
    const delay = Math.max(Date.parse(impersonation.expiresAt) - Date.now() + 1000, 0);
    const timer = setTimeout(() => {
      void reloadIdentity().catch(() => undefined);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [impersonation, reloadIdentity]);

  if (impersonation === null || user === null) return null;
  const name = `${user.firstName} ${user.lastName}`.trim();

  const leave = async () => {
    setLeaving(true);
    try {
      await endImpersonation();
    } catch (error) {
      toast({ variant: 'destructive', title: errorMessage(error, t) });
    } finally {
      setLeaving(false);
    }
  };

  return (
    <Alert variant="banner" data-tone="urgent" role="status" data-testid="banner-impersonation">
      <Eye className="h-4 w-4" />
      <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="min-w-0 flex-1 font-medium">
          {t('shell:impersonation.banner', { name })}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={leaving}
          onClick={() => void leave()}
          data-testid="button-impersonation-exit"
        >
          {t('shell:impersonation.exit')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Ramène à la console quand une séance se termine, quelle qu'en soit la cause : bouton
 * « Quitter », échéance, compte regardé désactivé entre-temps. Monté une fois, au-dessus des murs
 * et de la coque, pour survivre au passage de l'un à l'autre.
 */
export function useImpersonationExit(): void {
  const { impersonation, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const previous = useRef(impersonation);

  useEffect(() => {
    const ended = previous.current !== null && impersonation === null;
    previous.current = impersonation;
    if (ended && isAuthenticated) navigate(IMPERSONATION_EXIT_PATH, { replace: true });
  }, [impersonation, isAuthenticated, navigate]);
}
